import { useEffect, useState } from 'react';
import { AlertTriangle, Ban, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePagePermissions } from '../../hooks/usePagePermissions';
import { customerDuplicatesApi } from '../../services/customerDuplicatesApi';
import type { GroupForCustomer } from '../../types/customerDuplicates';

/**
 * "This customer also exists in dch, dit, dsp" — the link from `/customer-360` and
 * `/accounts-receivable` into the group view.
 *
 * ONE component, used by both pages, so the wording, the permission check and the failure
 * behaviour exist once. It renders NOTHING at all when:
 *   - the viewer has no view permission on `/customer-duplicates` (most people). The resolver
 *     endpoint is gated on that page for the same reason: its answer quotes the resource — how
 *     many accounts, in which companies, with what exposure — and a badge on another page must
 *     not leak what the page itself would refuse (`feedback_gate_everything_that_quotes_the_resource`);
 *   - the customer is in no group, which is the normal case;
 *   - anything at all goes wrong. A hint that fails to load must not put an error on a page it
 *     is only a guest on.
 *
 * The link addresses the group by CUSTOMER id, never by group id: a rebuild can give a group a
 * new row when its anchor member leaves, and a link that rots silently is worse than no link.
 *
 * This is a read-only addition. It changes no existing calculation on either host page.
 */
export function CustomerGroupLink({ customerId, className }: { customerId: string | null | undefined; className?: string }) {
    const { canView } = usePagePermissions('/customer-duplicates');
    const [group, setGroup] = useState<GroupForCustomer | null>(null);

    useEffect(() => {
        let cancelled = false;
        setGroup(null);
        if (!canView || !customerId) return;
        customerDuplicatesApi.groupForCustomer(customerId)
            .then(g => { if (!cancelled && g?.found) setGroup(g); })
            .catch(() => { /* no permission, no group, no network — the hint simply does not appear */ });
        return () => { cancelled = true; };
    }, [canView, customerId]);

    if (!group?.found) return null;

    const companies = (group.companyCodes ?? '').split(',').map(c => c.trim()).filter(Boolean);
    // "ALSO in" — so the company the reader is already looking at is dropped. A group whose
    // accounts all sit in one company still gets a hint, worded as what it is.
    const ownCompany = (customerId ?? '').split('-')[0];
    const otherCompanies = companies.filter(c => c !== ownCompany);
    const blocked = Number(group.blockedSplit ?? 0) > 0;
    const overdue = Number(group.overdueSplit ?? 0) > 0;
    const warns = blocked || overdue;

    return (
        <Link
            to={group.url ?? `/customer-duplicates?tab=groups&customer=${encodeURIComponent(customerId ?? '')}`}
            title={
                `${group.displayName ?? 'This customer'} is carried on ${group.memberCount} accounts in `
                + `${companies.join(', ')}. Total open AR across the group: `
                + `${Number(group.openAmountEur ?? 0).toLocaleString('de-CH', { maximumFractionDigits: 0 })} EUR.`
                + (blocked ? ' One company has an account BLOCKED while another can still trade.' : '')
                + (overdue ? ` One company holds items more than ${group.overdueDays} days overdue while another carries it clear.` : '')
            }
            className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs transition-colors ${
                warns
                    ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            } ${className ?? ''}`}
        >
            {blocked ? <Ban className="h-3 w-3 text-red-500" />
                : overdue ? <AlertTriangle className="h-3 w-3 text-amber-600" />
                    : <Users className="h-3 w-3 text-gray-400" />}
            <span>
                {otherCompanies.length > 0
                    ? <>Also in <span className="font-medium uppercase">{otherCompanies.join(', ')}</span></>
                    : <>Also on another account in <span className="font-medium uppercase">{ownCompany}</span></>}
                {' '}({group.memberCount} accounts)
            </span>
        </Link>
    );
}
